// photoUpload.js — upload one photo into the People_Photos library root.
// Contacts-local on purpose (invariant 3: nothing here touches figApi or
// LIST_DEFINITIONS). The bytes go from the browser straight to Graph; the
// Netlify relay and the App Service never see them.
//
// Name: nextPhotoName() over the caller's listing, conflictBehavior FAIL so a
// stale listing can never overwrite a file; on 409 the library is re-listed
// and the upload retried ONCE. Type: decided by the file's first bytes, never
// by file.type alone (Safari and drag-drop both lie).

import { nextPhotoName, sniffImageType, PHOTO_MAX_BYTES, SIMPLE_PUT_LIMIT, UPLOAD_CHUNK } from './photoNames';

const GRAPH = 'https://graph.microsoft.com/v1.0';
const PERSON_ID_RE = /^P-\d{5}$/;
const MIME = { jpg: 'image/jpeg', png: 'image/png' };

async function httpError(res, what) {
  let msg = res.statusText, code;
  try { const j = await res.json(); msg = j?.error?.message || msg; code = j?.error?.code; } catch { /* non-JSON body */ }
  const e = new Error(`${what} (${res.status})${msg ? `: ${msg}` : ''}`);
  e.status = res.status; e.code = code;
  return e;
}

async function readHead(file) {
  const blob = file.slice(0, 4);
  const buf = typeof blob.arrayBuffer === 'function' ? await blob.arrayBuffer() : await new Response(blob).arrayBuffer();
  return new Uint8Array(buf);
}

// ≤ 4 MB: one PUT.
async function simplePut(token, driveId, name, file, mime) {
  const res = await fetch(
    `${GRAPH}/drives/${driveId}/root:/${encodeURIComponent(name)}:/content?@microsoft.graph.conflictBehavior=fail`,
    { method: 'PUT', headers: { Authorization: `Bearer ${token}`, 'Content-Type': mime }, body: file },
  );
  if (!res.ok) throw await httpError(res, 'Upload failed');
  return res.json();
}

// > 4 MB: an upload session in 5 MiB chunks (FIGVault's pattern, minus the
// Content-Length header, which browsers set themselves and refuse from scripts).
async function sessionPut(token, driveId, name, file) {
  const sess = await fetch(
    `${GRAPH}/drives/${driveId}/root:/${encodeURIComponent(name)}:/createUploadSession`,
    { method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ item: { '@microsoft.graph.conflictBehavior': 'fail' } }) },
  );
  if (!sess.ok) throw await httpError(sess, 'Upload session failed');
  const { uploadUrl } = await sess.json();
  const total = file.size;
  let start = 0, last = null;
  while (start < total) {
    const end = Math.min(start + UPLOAD_CHUNK, total);
    const res = await fetch(uploadUrl, {
      method: 'PUT',
      headers: { 'Content-Range': `bytes ${start}-${end - 1}/${total}` },
      body: file.slice(start, end),
    });
    if (!res.ok && res.status !== 202) throw await httpError(res, 'Chunk upload failed');
    if (res.status === 200 || res.status === 201) last = await res.json();
    start = end;
  }
  return last;
}

/**
 * @param {string} token       Graph access token
 * @param {string} driveId     the People_Photos drive
 * @param {string} personId    P-NNNNN — the row must already exist
 * @param {File}   file        from an <input type=file>
 * @param {string[]} existingNames  the person's current gallery (any names; filtered by photoNameRe)
 * @param {() => Promise<string[]>} [relist]  fresh library names, used once on a 409
 * @returns {Promise<{name: string, quickXorHash: string|null}>}
 *   quickXorHash is whatever the driveItem response carried under file.hashes; null when absent.
 */
export async function uploadPhotoFile(token, driveId, personId, file, existingNames, relist) {
  if (!PERSON_ID_RE.test(String(personId || ''))) throw new Error('Save the contact before adding a photo.');
  if (!file || typeof file.size !== 'number') throw new Error('No file selected.');
  if (file.size === 0) throw new Error('The file is empty.');
  if (file.size > PHOTO_MAX_BYTES) throw new Error('Photo is too large (over 20 MB).');
  const ext = sniffImageType(await readHead(file));
  if (!ext) throw new Error('Only JPEG or PNG.');

  const put = name => (file.size <= SIMPLE_PUT_LIMIT
    ? simplePut(token, driveId, name, file, MIME[ext])
    : sessionPut(token, driveId, name, file));

  let name = nextPhotoName(personId, existingNames || [], ext);
  let item;
  try {
    item = await put(name);
  } catch (e) {
    if (e?.status !== 409 || typeof relist !== 'function') throw e;
    name = nextPhotoName(personId, await relist(), ext);   // someone else took the number; look again, once
    item = await put(name);
  }
  return { name: item?.name || name, quickXorHash: item?.file?.hashes?.quickXorHash || null };
}
